import { useCallback, useEffect, useState } from 'react'
import {
  Drawer,
  DrawerContent,
  DrawerNavigation,
} from '@progress/kendo-react-layout'
import { FaTimesCircle } from 'react-icons/fa'
import { useSearchParams } from 'react-router-dom'

import PurchaseOrderGrid from '../../components/grids/PurchaseOrders/PurchaseOrderGrid'
import PurchaseOrderForm from '../../components/FormComps/PurchaseOrders/PurchaseOrderForm'
import { getPurchaseOrders } from '../../data/purchaseOrderData'
import { purchaseOrderGrid } from '../../data/interfaces'
import { useToast } from '../../contexts/ToastContext'
import '../Styles.css'

interface PurchaseOrdersProps {
  lockEdit: boolean
}

const PurchaseOrders = (props: PurchaseOrdersProps) => {
  const { showToast } = useToast()

  const [expanded, setExpanded] = useState(false)
  const [addFormVisible, setAddFormVisible] = useState(false)
  const [purchaseOrderId, setPurchaseOrderId] = useState<number | null>(null)

  const [data, setData] = useState<purchaseOrderGrid[]>([])
  const [month, setMonth] = useState<number | null>(null)
  const [showDrafts, setShowDrafts] = useState(true)
  const [showDeleted, setShowDeleted] = useState(false)

  const [searchParams] = useSearchParams()

  const fetchData = useCallback(async () => {
    try {
      setData(await getPurchaseOrders({ month, showDrafts, showDeleted }))
    } catch (error) {
      console.error('Error fetching purchase orders:', error)
      showToast('Failed to load purchase orders', 'error')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, showDrafts, showDeleted])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Deep link from elsewhere in the portal, e.g. a job or a Pieces allocation.
  useEffect(() => {
    const id = searchParams.get('id')
    if (id) {
      setPurchaseOrderId(Number(id))
      setAddFormVisible(false)
      setExpanded(true)
    }
  }, [searchParams])

  const closeDrawer = () => {
    setExpanded(false)
    setAddFormVisible(false)
    setPurchaseOrderId(null)
  }

  const handleAdd = () => {
    setPurchaseOrderId(null)
    setAddFormVisible(true)
    setExpanded(true)
  }

  const handleEdit = (id: number) => {
    setPurchaseOrderId(id)
    setAddFormVisible(false)
    setExpanded(true)
  }

  return (
    <div>
      <Drawer
        expanded={expanded}
        position={'end'}
        width={900}
        mode='overlay'
        animation={{
          duration: 400,
        }}
        onOverlayClick={closeDrawer}
        onSelect={() => setExpanded(false)}
      >
        <DrawerContent>
          <DrawerNavigation>
            {(addFormVisible || purchaseOrderId) && (
              <>
                <div className='form__title__container'>
                  <h1 className='form__title'>
                    {addFormVisible ? 'New Purchase Order' : 'Purchase Order'}
                  </h1>
                  <FaTimesCircle className='closeform' onClick={closeDrawer} />
                </div>
                <PurchaseOrderForm
                  key={purchaseOrderId ?? 'new'}
                  purchaseOrderId={purchaseOrderId}
                  lockEdit={props.lockEdit}
                  onSaved={(order) => {
                    fetchData()
                    // A brand new order becomes the one being edited, so its
                    // lines can be worked on without reopening the drawer.
                    if (order && !purchaseOrderId) {
                      setPurchaseOrderId(order.purchaseOrderId)
                      setAddFormVisible(false)
                    }
                  }}
                  onClose={closeDrawer}
                />
              </>
            )}
          </DrawerNavigation>

          <PurchaseOrderGrid
            lockEdit={props.lockEdit}
            onData={data}
            onAddToggle={handleAdd}
            onEditToggle={handleEdit}
            onMonthCreatedBy={(value) => setMonth(value > 0 ? value : null)}
            onShowDrafts={setShowDrafts}
            onShowDeleted={setShowDeleted}
            showDrafts={showDrafts}
            showDeleted={showDeleted}
          />
        </DrawerContent>
      </Drawer>
    </div>
  )
}

export default PurchaseOrders
